/**
 * Condition Evaluator
 *
 * Evaluates JSON-based condition expressions against AspectCounts.
 * This replaces JavaScript functions in PLACEMENT_STRATEGIES and PLACEMENT_SPACE_STRATEGIES
 * with a data-driven approach that can be stored in the database.
 */

import type {
    Condition,
    AspectCounts,
    EqualsCondition,
    GteCondition,
    LteCondition,
    SumEqualsCondition,
    SumGteCondition,
    SumLteCondition,
    AndCondition,
    OrCondition,
    NotCondition,
} from '@/types/conditionTypes'

/**
 * Evaluates a condition against the given aspect counts.
 * @param condition - The condition to evaluate (can be nested)
 * @param counts - The aspect counts object with product type counts
 * @returns true if the condition is satisfied, false otherwise
 */
export function evaluateCondition(condition: Condition, counts: AspectCounts): boolean {
    switch (condition.type) {
        case 'equals':
            return evaluateEquals(condition, counts)

        case 'gte':
            return evaluateGte(condition, counts)

        case 'lte':
            return evaluateLte(condition, counts)

        case 'sumEquals':
            return evaluateSumEquals(condition, counts)

        case 'sumGte':
            return evaluateSumGte(condition, counts)

        case 'sumLte':
            return evaluateSumLte(condition, counts)

        case 'and':
            return evaluateAnd(condition, counts)

        case 'or':
            return evaluateOr(condition, counts)

        case 'not':
            return evaluateNot(condition, counts)

        case 'always':
            return true

        default:
            console.warn(`Unknown condition type: ${(condition as Condition).type}`)
            return false
    }
}

// Helper: Get field value from counts with fallback to 0
function getFieldValue(counts: AspectCounts, field: string): number {
    return counts[field] ?? 0
}

// Equals: field === value
function evaluateEquals(condition: EqualsCondition, counts: AspectCounts): boolean {
    return getFieldValue(counts, condition.field) === condition.value
}

// Greater than or equal: field >= value
function evaluateGte(condition: GteCondition, counts: AspectCounts): boolean {
    return getFieldValue(counts, condition.field) >= condition.value
}

// Less than or equal: field <= value
function evaluateLte(condition: LteCondition, counts: AspectCounts): boolean {
    return getFieldValue(counts, condition.field) <= condition.value
}

// Sum equals: sum(fields) === value
function evaluateSumEquals(condition: SumEqualsCondition, counts: AspectCounts): boolean {
    const sum = condition.fields.reduce((acc, field) => acc + getFieldValue(counts, field), 0)
    return sum === condition.value
}

// Sum >= value
function evaluateSumGte(condition: SumGteCondition, counts: AspectCounts): boolean {
    const sum = condition.fields.reduce((acc, field) => acc + getFieldValue(counts, field), 0)
    return sum >= condition.value
}

// Sum <= value
function evaluateSumLte(condition: SumLteCondition, counts: AspectCounts): boolean {
    const sum = condition.fields.reduce((acc, field) => acc + getFieldValue(counts, field), 0)
    return sum <= condition.value
}

// And: all conditions must be true
function evaluateAnd(condition: AndCondition, counts: AspectCounts): boolean {
    return condition.conditions.every((c) => evaluateCondition(c, counts))
}

// Or: any condition must be true
function evaluateOr(condition: OrCondition, counts: AspectCounts): boolean {
    return condition.conditions.some((c) => evaluateCondition(c, counts))
}

// Not: negation
function evaluateNot(condition: NotCondition, counts: AspectCounts): boolean {
    return !evaluateCondition(condition.condition, counts)
}
